import type { MessageKey } from '../i18n/messages';
import type { ActivityConfig } from '../../features/profile/api';
import { checkinRewardText } from './checkinDays';

type Reward = { kind?: string; amount?: number; ticket_count?: number; coupon_code?: string };

/**
 * Human label of the `bind_contact` reward for a channel — e.g. "R$ 5,00",
 * "1 bilhete", "R$ 10 em cupom" — derived from the SAME activity config that
 * the rewards center uses, so the verify sheet promises exactly what the
 * activity pays (fixes the old hardcoded "1 bilhete" that ignored the real
 * config, D-08).
 *
 * Returns '' when nothing is configured/enabled for that channel; the caller
 * then drops the reward promise instead of inventing one.
 */
export function bindContactRewardLabel(
  configs: ActivityConfig[] | undefined,
  channel: 'phone' | 'email',
  t: (key: MessageKey) => string,
): string {
  const bind = (configs || []).find((c) => c.activityType === 'bind_contact' && c.enabled);
  const cfg = bind?.config as Record<string, unknown> | undefined;
  if (!cfg) return '';
  const num = (k: string) => Number(cfg[k] as number) || 0;
  const str = (k: string) => String(cfg[k] ?? '');
  const configured = (channel === 'phone' ? cfg.phone_rewards : cfg.email_rewards) as Reward[] | undefined;
  let rewards: Reward[];
  if (configured?.length) {
    rewards = configured;
  } else {
    const kind = channel === 'phone' ? str('phone_reward_kind') : str('email_reward_kind');
    if (kind === 'coupon') {
      rewards = [{ kind, coupon_code: channel === 'phone' ? str('phone_coupon_code') : str('email_coupon_code') }];
    } else if (kind === 'raffle_ticket') {
      rewards = [{ kind, ticket_count: channel === 'phone' ? num('phone_ticket_count') : num('email_ticket_count') }];
    } else {
      rewards = [{ kind: 'cash', amount: channel === 'phone' ? num('phone_amount') : num('email_amount') }];
    }
  }
  rewards = rewards.filter((r) => Number(r.amount || 0) > 0 || Number(r.ticket_count || 0) > 0 || Boolean(r.coupon_code));
  if (!rewards.length) return '';
  return checkinRewardText(rewards as Parameters<typeof checkinRewardText>[0], t);
}
